import type { TLineChartData } from "@ch/components/Charts/LineChart";
import { ForumsRoutesEnum } from "@ch/router/Forums/enums";
import { HealthDiaryRoutesEnum } from "@ch/router/HealthDiaryRoutes/enums";
import { HealthPassportRoutesEnum } from "@ch/router/HealthPassportRoutes/enums";
import { MindfulnessRoutesEnum } from "@ch/router/MindfulnessRoutes/enums";
import { SubscriptionRoutesEnum } from "@ch/router/SubscriptionRoutes/enums";
import { SymptomTrackerRoutesEnum } from "@ch/router/SymptomTrackerRoutes/enums";
import type { TBarChartData } from "@ch/components/Charts/BarChart.tsx";
import type { TDashboardRouteCard } from "./types";

export const healthPassportLocationOptions = [
  { label: "Outreach", value: "Outreach" },
  { label: "Hospital", value: "Hospital" },
  { label: "Primary Health Centre", value: "Primary Health Centre" },
  { label: "Laboratory", value: "Laboratory" },
];

export const dosageUnitOptions = [
  {
    label: "mg",
    value: "mg",
  },
  {
    label: "g",
    value: "g",
  },
  {
    label: "ml",
    value: "ml",
  },
  {
    label: "iu",
    value: "iu",
  },
];

export const sourceOptions = [
  {
    value: "doctor",
    label: "Doctor",
  },
  {
    value: "hospital",
    label: "Hospital",
  },
  {
    value: "nurse",
    label: "Nurse",
  },
  {
    value: "self-prescribed",
    label: "Self Prescribed",
  },
  {
    value: "pharmacy",
    label: "Pharmacy",
  },
  {
    value: "family-and-friends",
    label: "Family & Friends",
  },
  {
    value: "chemist",
    label: "Chemist",
  },
  {
    value: "self-reported",
    label: "Self Reported",
  },
];

export const sourceOptionsPrescriptions = [
  {
    value: "doctor",
    label: "Doctor",
  },
  {
    value: "hospital",
    label: "Hospital",
  },
  {
    value: "nurse",
    label: "Nurse",
  },
  {
    value: "self-prescribed",
    label: "Self Prescribed",
  },
  {
    value: "pharmacy",
    label: "Pharmacy",
  },
  {
    value: "family-and-friends",
    label: "Family & Friends",
  },
  {
    value: "chemist",
    label: "Chemist",
  },
  {
    value: "self-reported",
    label: "Self Reported",
  },
];

export const educationalLevels = [
  { label: "None", value: "none" },
  { label: "Primary Education", value: "primary" },
  { label: "Secondary Education", value: "secondary" },
  { label: "Tertiary Education", value: "tetiary" },
  { label: "Post Graduate Degree", value: "masters" },
  { label: "Doctorate Degree (PhD)", value: "doctorate" },
];

export const chronicDiseases = [
  {
    label: "High blood pressure",
    value: "high_blood_pressure",
  },
  {
    label: "Diabetes",
    value: "diabetes",
  },
  {
    label: "Overweight/Obesity",
    value: "obesity",
  },
  {
    label: "Cancer",
    value: "cancer",
  },
  {
    label: "Anxiety",
    value: "anxiety",
  },
  {
    label: "Depression",
    value: "depression",
  },
  {
    label: "None",
    value: "none",
  },
];

export const userFeedbackOptions = [
  { value: "bug_report", label: "Bug report" },
  { value: "feature_request", label: "Feature request" },
  { value: "general_comment", label: "General comment" },
  { value: "other", label: "Other" },
];

export const eventTypes = {
  facility_appointment: "Hospital appointment",
  antenatal_appointment: "Antenatal appointment",
  coach_appointment: "Coach appointment",
  general_event: "mDoc event",
  personal_reminder: "Reminder",
  webinar: "Webinar",
  anc: "ANC",
  pnc: "PNC",
  family_planning: "Family Planning",
};

export const dashboardRouteCards: TDashboardRouteCard[] = [
  {
    id: "1",
    title: "Forums",
    icon: "forum",
    iconContainerClassName: "bg-blue-400 text-blue",
    onClick: (callback) => callback?.(ForumsRoutesEnum.ROOT),
  },
  {
    id: "2",
    title: "Mindfulness",
    icon: "mindfulness",
    iconContainerClassName: "bg-green-200 text-green",
    onClick: (callback) => callback?.(MindfulnessRoutesEnum.ROOT),
  },
  {
    id: "3",
    title: "Health Diary",
    icon: "health-diary",
    iconContainerClassName: "bg-yellow-400 text-yellow",
    onClick: (callback) => callback?.(HealthDiaryRoutesEnum.ROOT),
  },
  {
    id: "4",
    title: "Health Passport",
    icon: "health-passport",
    iconContainerClassName: "bg-gray-200 text-gray",
    onClick: (callback) => callback?.(HealthPassportRoutesEnum.ROOT),
  },
  {
    id: "5",
    title: "My Programs",
    icon: "subscription",
    iconContainerClassName: "bg-orange-400 text-orange",
    onClick: (callback) => callback?.(SubscriptionRoutesEnum.ROOT),
  },
  {
    id: "6",
    title: "Symptom Tracker",
    icon: "radar",
    iconContainerClassName: "bg-purple-400 text-purple",
    onClick: (callback) => callback?.(SymptomTrackerRoutesEnum.ROOT),
  },
];

export const lineChartDefaultDataSetConfig: Partial<
  TLineChartData["datasets"][0]
> = {
  xAxisID: "x",
  tension: 0.1,
  pointStyle: "circle",
  pointRadius: 3,
  pointHoverRadius: 5,
  borderWidth: 2,
};

export const barChartDefaultDataSetConfig: Partial<
  TBarChartData["datasets"][0]
> = {
  xAxisID: "x",
};

export const mealOptions = [
  { label: "Breakfast", value: "breakfast" },
  { label: "Dinner", value: "dinner" },
  { label: "Fasting", value: "fasting" },
  { label: "Lunch", value: "lunch" },
  { label: "Random", value: "random" },
  { label: "Snacks", value: "snack" },
] as const;

export const mealOptionsValues = mealOptions.map(({ value }) => value);

export type TMealOptionValues = (typeof mealOptionsValues)[number];

export const tagVariants = {
  active: {
    container: "bg-orange-400",
    text: "text-orange",
  },
  inactive: {
    container: "bg-gray-200",
    text: "text-gray",
  },
} as const;

export const pregnancyStateText = {
  active: "In Progress",
  inactive: "Closed",
} as const;

export const bPBodyPositionOptions = [
  { label: "Sitting", value: "sitting" },
  {
    label: "Lying on the back (Supine)",
    value: "lying on the back (supine)",
  },
  {
    label: "Lying on the left side (Left lateral)",
    value: "lying on the left side (left lateral)",
  },
  {
    label: "Lying on the right side (Right lateral)",
    value: "lying on the right side (right lateral)",
  },
  { label: "Standing", value: "standing" },
];

export const CUSTOM_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
